import { differenceInDays } from "date-fns";
import type { ReactNode } from "react";
import React, { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useSeriesArticleSectionTag } from "scmp-app/components/article/article-render/series-article/hooks";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { parseDate } from "scmp-app/lib/utils";
import type { seriesBannerContent$key } from "scmp-app/queries/__generated__/seriesBannerContent.graphql";

import {
  Container,
  Content,
  CoverImage,
  CoverImageContainer,
  Headline,
  NewTag,
  PublishedDate,
  SeriesArticleSectionTag,
  StyledBaseLink,
  Summary,
} from "./styles";

export type Props = {
  reference: seriesBannerContent$key;
  share: ReactNode;
} & ContentItemRenderVariantProps;

const Component = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, share, topicLinkVariant }, reference) => {
    const content = useFragment(
      graphql`
        fragment seriesBannerContent on Content {
          ...hooksSeriesArticleSectionTagArticle
          ...entityLink
          ...hooksContentItemProvidersContent
            @arguments(
              withCoverImage: true
              withCoverImageSize540x360: true
              withCoverImageLandscape250x99: true
              withHeadline: true
              withSummary: true
              withPublishedDate: true
            )
          publishedDate
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content, topicLinkVariant);
    const headline = providers.headline({ hideTooltip: true, preferSocialHeadline: true });
    const seriesArticleSectionTag = useSeriesArticleSectionTag(content);

    const isShowNewSeries = () => {
      if (!content.publishedDate) return false;
      const diff = differenceInDays(new Date(), parseDate(content.publishedDate));
      return diff <= 30;
    };

    return (
      <Container className={className} ref={reference}>
        <Content>
          <SeriesArticleSectionTag>
            <StyledBaseLink as="/series" pathname="/series">
              {isShowNewSeries() && <NewTag>NEW</NewTag>}
              {seriesArticleSectionTag}
            </StyledBaseLink>
          </SeriesArticleSectionTag>

          <Headline>{headline}</Headline>

          <Summary>
            {providers.summary({
              isTextOverflowEllipsis: true,
            })}
          </Summary>
          {share}
          <PublishedDate>Updated: {providers.publishedDate()}</PublishedDate>
        </Content>
        <CoverImageContainer>
          <CoverImage>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "landscape250x99",
                mobileUp: "size540x360",
                tabletUp: "landscape250x99",
              },
            })}
          </CoverImage>
        </CoverImageContainer>
      </Container>
    );
  },
);

Component.displayName = "ContentItemSeriesBanner";

export const ContentItemSeriesBanner = Component;
